import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { FEATURE_NAMES } from '../../../data/nurseAppModels';

const FeatureConfigPanel = () => {
  const { currentUser } = useAuth();
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchFeatureConfig();
  }, []);

  const fetchFeatureConfig = async () => {
    try {
      const configRef = doc(db, 'featureConfig', 'SYSTEM_FEATURES');
      const snapshot = await getDoc(configRef);
      
      if (snapshot.exists()) {
        setFeatures(snapshot.data().features || {});
      } else {
        // Create default config
        const defaultFeatures = {
          shiftManagement: {
            enabled: true,
            enabledAt: new Date(),
            enabledBy: 'SYSTEM',
            reason: 'Core feature - Phase 1'
          },
          equipmentManagement: {
            enabled: true,
            enabledAt: new Date(),
            enabledBy: 'SYSTEM',
            reason: 'Core feature - Phase 1'
          },
          teamManagement: {
            enabled: true,
            enabledAt: new Date(),
            enabledBy: 'SYSTEM',
            reason: 'Core feature - Phase 1'
          },
          gamification: {
            enabled: false,
            reason: 'Phase 2 - ให้ผู้ใช้เรียนรู้ระบบพื้นฐานก่อน'
          },
          marketplace: {
            enabled: false,
            reason: 'Phase 2 - ให้ผู้ใช้เรียนรู้ระบบพื้นฐานก่อน'
          },
          giftSystem: {
            enabled: false,
            reason: 'Phase 2 - ต้องเปิด Gamification ก่อน'
          },
          mascotRanking: {
            enabled: false,
            reason: 'Phase 2 - ต้องเปิด Gamification ก่อน'
          }
        };
        setFeatures(defaultFeatures);
      }
    } catch (error) {
      console.error('Error fetching feature config:', error);
      setError('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = async (featureName, enabled) => {
    if (!currentUser) {
      setError('กรุณาเข้าสู่ระบบ');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const configRef = doc(db, 'featureConfig', 'SYSTEM_FEATURES');
      const snapshot = await getDoc(configRef);
      
      const currentFeatures = snapshot.exists() 
        ? snapshot.data().features 
        : features;
      
      const updatedFeatures = {
        ...currentFeatures,
        [featureName]: {
          ...currentFeatures[featureName],
          enabled: enabled,
          enabledAt: enabled ? new Date() : currentFeatures[featureName]?.enabledAt,
          disabledAt: !enabled ? new Date() : null,
          enabledBy: enabled ? currentUser.uid : currentFeatures[featureName]?.enabledBy,
          updatedAt: new Date()
        }
      };

      // Get or create history
      const configData = snapshot.exists() ? snapshot.data() : {};
      const history = configData.changeHistory || [];
      history.push({
        feature: featureName,
        action: enabled ? 'ENABLED' : 'DISABLED',
        by: currentUser.uid,
        byName: currentUser.displayName || currentUser.email,
        at: new Date(),
        reason: enabled ? 'เปิดใช้งานโดย Admin' : 'ปิดใช้งานโดย Admin'
      });

      if (snapshot.exists()) {
        await updateDoc(configRef, {
          features: updatedFeatures,
          changeHistory: history.slice(-100), // Keep last 100
          updatedAt: new Date(),
          updatedBy: currentUser.uid
        });
      } else {
        await setDoc(configRef, {
          configId: 'SYSTEM_FEATURES',
          version: 1,
          features: updatedFeatures,
          changeHistory: history,
          rolloutPlan: {
            phase: 1,
            currentPhase: 1,
            phases: [
              {
                phase: 1,
                name: 'Core Features',
                features: ['shiftManagement', 'equipmentManagement', 'teamManagement'],
                startDate: new Date(),
                status: 'ACTIVE'
              },
              {
                phase: 2,
                name: 'Advanced Features',
                features: ['gamification', 'marketplace', 'giftSystem', 'mascotRanking'],
                startDate: null,
                status: 'PLANNED'
              }
            ]
          },
          updatedAt: new Date(),
          updatedBy: currentUser.uid
        });
      }

      setFeatures(updatedFeatures);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error toggling feature:', error);
      setError('ไม่สามารถบันทึกได้: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const featureLabels = {
    shiftManagement: 'Shift Management',
    equipmentManagement: 'Equipment Management',
    teamManagement: 'Team Management',
    gamification: 'Gamification',
    marketplace: 'Marketplace',
    giftSystem: 'Gift System',
    mascotRanking: 'Mascot Ranking'
  };

  const featureDescriptions = {
    shiftManagement: 'บริหารจัดการเวร',
    equipmentManagement: 'บริหารจัดการอุปกรณ์',
    teamManagement: 'ระบบบริหารจัดการทีม',
    gamification: 'ระบบเกมมิฟิเคชัน',
    marketplace: 'ตลาดสินค้าสำหรับผู้หญิง',
    giftSystem: 'ระบบให้ของขวัญ',
    mascotRanking: 'ระบบ Mascot Ranking'
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Feature Configuration</h1>
        <p className="text-gray-600">จัดการการเปิด/ปิด Feature ต่างๆ ของระบบ</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          บันทึกสำเร็จ!
        </div>
      )}

      {/* Core Features */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Core Features (Phase 1)</h2>
        <div className="space-y-4">
          {['shiftManagement', 'equipmentManagement', 'teamManagement'].map((featureName) => (
            <div key={featureName} className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-1">
                    {featureLabels[featureName]}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {featureDescriptions[featureName]}
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`px-2 py-1 rounded ${features[featureName]?.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {features[featureName]?.enabled ? '✅ เปิดใช้งาน' : '❌ ปิดใช้งาน'}
                    </span>
                    {features[featureName]?.enabledAt && (
                      <span className="text-gray-500">
                        เปิดเมื่อ: {new Date(features[featureName].enabledAt.seconds * 1000).toLocaleString('th-TH')}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => toggleFeature(featureName, !features[featureName]?.enabled)}
                  disabled={saving}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    features[featureName]?.enabled
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {features[featureName]?.enabled ? 'ปิด' : 'เปิด'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Advanced Features */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Advanced Features (Phase 2)</h2>
        <div className="space-y-4">
          {['gamification', 'marketplace', 'giftSystem', 'mascotRanking'].map((featureName) => (
            <div key={featureName} className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-1">
                    {featureLabels[featureName]}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {featureDescriptions[featureName]}
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`px-2 py-1 rounded ${features[featureName]?.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {features[featureName]?.enabled ? '✅ เปิดใช้งาน' : '❌ ปิดใช้งาน'}
                    </span>
                    {features[featureName]?.enabledAt && (
                      <span className="text-gray-500">
                        เปิดเมื่อ: {new Date(features[featureName].enabledAt.seconds * 1000).toLocaleString('th-TH')}
                      </span>
                    )}
                  </div>
                  {features[featureName]?.reason && (
                    <p className="text-xs text-gray-500 mt-1">
                      {features[featureName].reason}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => toggleFeature(featureName, !features[featureName]?.enabled)}
                  disabled={saving}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    features[featureName]?.enabled
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {features[featureName]?.enabled ? 'ปิด' : 'เปิด'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeatureConfigPanel;


